import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import {
  normalizeInteractiveFieldValues,
  parseErrorMessage,
  serializeFormTemplateMetaData,
} from 'utils/helper';
import { NormalizeFormTemplateMetaDataResponse } from 'types/normalizedFormTemplate';
import { FormFieldsData } from 'components/anvilFormFillable';
import { MultiMediaResponseDTO } from 'artifacts/dtos';
import {
  useCompleteFormWorkflowPatientStep,
  useFormWorkflowRenderingMetadata,
  useGetFormWorkflowPdfs,
  useGetPatientMultimediaByMultiMediaId,
} from 'hooks/useApi';

type UsePatientFormWorkflowOptions = {
  formWorkflowId?: number | null;
  enabled?: boolean;
  viewCompletedMedias?: boolean;
  onSubmitSuccess?: () => void;
};

export function usePatientFormWorkflow({
  formWorkflowId,
  enabled = true,
  viewCompletedMedias = false,
  onSubmitSuccess,
}: UsePatientFormWorkflowOptions) {
  const [signedMultimedia, setSignedMultimedia] = useState<MultiMediaResponseDTO | undefined>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isFinalizingSubmission, setIsFinalizingSubmission] = useState(false);

  const isQueryEnabled = enabled && !isNil(formWorkflowId);
  const isSignMode = isQueryEnabled && !viewCompletedMedias;
  const isViewCompletedMode = isQueryEnabled && viewCompletedMedias;

  const {
    data: formTemplateMetaData,
    error: formTemplateError,
    isFetching: isFetchingFormTemplate,
  } = useFormWorkflowRenderingMetadata(
    formWorkflowId ?? undefined,
    isSignMode,
  );

  const {
    data: completedWorkflowPdfs,
    error: completedWorkflowPdfsError,
    isFetching: isFetchingCompletedWorkflowPdfs,
  } = useGetFormWorkflowPdfs(
    formWorkflowId ?? undefined,
    isViewCompletedMode,
  );

  const {
    mutateAsync: completePatientStep,
    isPending: loadingSign,
    error: submitError,
  } = useCompleteFormWorkflowPatientStep();

  const { mutateAsync: getMultimediaById } = useGetPatientMultimediaByMultiMediaId();

  const data = useMemo<NormalizeFormTemplateMetaDataResponse | undefined>(
    () => (formTemplateMetaData ? serializeFormTemplateMetaData(formTemplateMetaData) : undefined),
    [formTemplateMetaData],
  );

  useEffect(() => {
    if (!isNil(formTemplateError)) {
      setErrorMessage(parseErrorMessage(formTemplateError));
    }
  }, [formTemplateError]);

  useEffect(() => {
    if (!isNil(completedWorkflowPdfsError)) {
      setErrorMessage(parseErrorMessage(completedWorkflowPdfsError));
    }
  }, [completedWorkflowPdfsError]);

  useEffect(() => {
    if (!isNil(completedWorkflowPdfs)) {
      setSignedMultimedia(completedWorkflowPdfs);
    }
  }, [completedWorkflowPdfs]);

  const onSubmit = async (formData: FormFieldsData) => {
    if (isNil(formWorkflowId) || viewCompletedMedias) {
      return;
    }

    setIsFinalizingSubmission(true);

    try {
      const response = await completePatientStep({
        formWorkflowId,
        interactiveFieldValues: normalizeInteractiveFieldValues(formData) ?? [],
      });

      const mediaIds = compact(
        (response.workflow?.medias ?? []).map((media) => media.patientMultiMediaId),
      );

      if (mediaIds.length > 0) {
        const mediaResponses = await Promise.all(
          mediaIds.map((mediaId) => getMultimediaById(mediaId)),
        );
        const list = compact(mediaResponses.flatMap((mediaResponse) => mediaResponse.list ?? []));
        setSignedMultimedia({ list });
      }

      onSubmitSuccess?.();
    } catch (error) {
      setErrorMessage(parseErrorMessage(error));
    } finally {
      setIsFinalizingSubmission(false);
    }
  };

  const loading = isFetchingFormTemplate
    || isFetchingCompletedWorkflowPdfs
    || loadingSign
    || isFinalizingSubmission;

  const errorMessages = useMemo(
    () => compact([errorMessage, submitError ? parseErrorMessage(submitError) : '']).join(' '),
    [errorMessage, submitError],
  );

  return {
    data,
    loading,
    errorMessages,
    signedMultimedia,
    onSubmit,
  };
}
